import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {MaterialsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import {CameraControlsSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import {MeshStandardBuilderMatNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshStandardBuilder';
import {ParamType} from '../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';
import {CameraCSSRendererSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraCSSRenderer';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_CSS2DObject1(geo1: GeoObjNode) {
			var CSS2DObject1 = geo1.createNode('CSS2DObject');
			CSS2DObject1.setName('CSS2DObject1');
			const CSS2DObject1_nodes: ReturnedNodeDataDict = {};
			CSS2DObject1.uiData.setPosition(-300, 500);
			CSS2DObject1.flags.display.set(true);
			CSS2DObject1.p.overrideClassName.set(false);
			CSS2DObject1.p.overrideHTML.set(false);
			CSS2DObject1.p.html.set('<div>default html2</div>');
			CSS2DObject1.params.postCreateSpareParams();
			CSS2DObject1.params.runOnSceneLoadHooks();
			return {node: CSS2DObject1, children: CSS2DObject1_nodes};
		}
		function create_data1(geo1: GeoObjNode) {
			var data1 = geo1.createNode('data');
			data1.setName('data1');
			const data1_nodes: ReturnedNodeDataDict = {};
			data1.uiData.setPosition(-100, 100);
			data1.p.data.set(
				'[{"P":[0,0,-1],"htmlClass":"CSS2DObject","html": "<div>left</div>"}, {"P":[0,0,1],"htmlClass":"CSS2DObject2","html": "<div>right</div>"}]'
			);
			data1.params.postCreateSpareParams();
			data1.params.runOnSceneLoadHooks();
			return {node: data1, children: data1_nodes};
		}
		function create_emptyObject1(geo1: GeoObjNode) {
			var emptyObject1 = geo1.createNode('emptyObject');
			emptyObject1.setName('emptyObject1');
			const emptyObject1_nodes: ReturnedNodeDataDict = {};
			emptyObject1.uiData.setPosition(-400, 100);
			emptyObject1.params.postCreateSpareParams();
			emptyObject1.params.runOnSceneLoadHooks();
			return {node: emptyObject1, children: emptyObject1_nodes};
		}
		function create_copy1(geo1: GeoObjNode) {
			var copy1 = geo1.createNode('copy');
			copy1.setName('copy1');
			const copy1_nodes: ReturnedNodeDataDict = {};
			copy1.uiData.setPosition(-300, 300);
			copy1.p.copyAttributes.set(true);
			copy1.p.attributesToCopy.set('html htmlClass');
			copy1.params.postCreateSpareParams();
			copy1.params.runOnSceneLoadHooks();
			return {node: copy1, children: copy1_nodes};
		}
		geo1_nodes['CSS2DObject1'] = create_CSS2DObject1(geo1);
		geo1_nodes['data1'] = create_data1(geo1);
		geo1_nodes['emptyObject1'] = create_emptyObject1(geo1);
		geo1_nodes['copy1'] = create_copy1(geo1);
		geo1_nodes['CSS2DObject1'].node.setInput(0, geo1_nodes['copy1'].node);
		geo1_nodes['copy1'].node.setInput(0, geo1_nodes['emptyObject1'].node);
		geo1_nodes['copy1'].node.setInput(1, geo1_nodes['data1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([]);
		}
		geo1.uiData.setPosition(-50, -350);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	function create_ground(parentNode: RootManagerNode) {
		var ground = parentNode.createNode('geo');
		ground.setName('ground');
		const ground_nodes: ReturnedNodeDataDict = {};
		function create_material1(ground: GeoObjNode) {
			var material1 = ground.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandardBuilder1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(ground: GeoObjNode) {
			var MAT = ground.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandardBuilder1(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder1 = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder1.setName('meshStandardBuilder1');
				const meshStandardBuilder1_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder1.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-450, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder1.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec3ToFloat1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var vec3ToFloat1 = meshStandardBuilder1.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-200, 200);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var floatToVec2_1 = meshStandardBuilder1.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-100, 200);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_checkers1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var checkers1 = meshStandardBuilder1.createNode('checkers');
					checkers1.setName('checkers1');
					const checkers1_nodes: ReturnedNodeDataDict = {};
					checkers1.uiData.setPosition(50, 200);
					checkers1.params.postCreateSpareParams();
					checkers1.params.runOnSceneLoadHooks();
					return {node: checkers1, children: checkers1_nodes};
				}
				function create_mix1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder1.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {spare: true, editable: false});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder1.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(50, -150);
					constant1.p.type.set(4);
					constant1.p.color.set([0.24313725490196078, 0.5098039215686274, 0.8549019607843137]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder1.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(0, 0);
					constant2.p.type.set(4);
					constant2.p.color.set([0.047058823529411764, 0.10196078431372549, 0.17647058823529413]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				meshStandardBuilder1_nodes['globals1'] = create_globals1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'] = create_output1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['floatToVec2_1'] = create_floatToVec2_1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['checkers1'] = create_checkers1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['mix1'] = create_mix1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant1'] = create_constant1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant2'] = create_constant2(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'].node.setInput(
					'color',
					meshStandardBuilder1_nodes['mix1'].node,
					'mix',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder1_nodes['globals1'].node,
					'position',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'x',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'z',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['checkers1'].node.setInput(
					'uv',
					meshStandardBuilder1_nodes['floatToVec2_1'].node,
					'vec2',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder1_nodes['constant1'].node,
					'val',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder1_nodes['constant2'].node,
					'val',
					{noExceptionOnInvalidInput: true}
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder1_nodes['checkers1'].node,
					'checker',
					{noExceptionOnInvalidInput: true}
				);
				if (meshStandardBuilder1.childrenController) {
					meshStandardBuilder1.childrenController.selection.set([]);
				}
				meshStandardBuilder1.uiData.setPosition(0, 200);
				meshStandardBuilder1.params.postCreateSpareParams();
				meshStandardBuilder1.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder1, children: meshStandardBuilder1_nodes};
			}
			MAT_nodes['meshStandardBuilder1'] = create_meshStandardBuilder1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_box1(ground: GeoObjNode) {
			var box1 = ground.createNode('box');
			box1.setName('box1');
			const box1_nodes: ReturnedNodeDataDict = {};
			box1.uiData.setPosition(0, 100);
			box1.p.sizes.set([9.98, 1, 9.98]);
			box1.p.center.set([0, -0.5, 0]);
			box1.params.postCreateSpareParams();
			box1.params.runOnSceneLoadHooks();
			return {node: box1, children: box1_nodes};
		}
		ground_nodes['material1'] = create_material1(ground);
		ground_nodes['MAT'] = create_MAT(ground);
		ground_nodes['box1'] = create_box1(ground);
		ground_nodes['material1'].node.setInput(0, ground_nodes['box1'].node);
		if (ground.childrenController) {
			ground.childrenController.selection.set([]);
		}
		ground.uiData.setPosition(-50, -450);
		ground.flags.display.set(true);
		ground.params.postCreateSpareParams();
		ground.params.runOnSceneLoadHooks();
		return {node: ground, children: ground_nodes};
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(25.2);
			polarTransform1.p.depth.set(3);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([lights_nodes['hemisphereLight1'].node]);
		}
		lights.uiData.setPosition(-50, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_perspectiveCamera1(cameras: GeoObjNode) {
			var perspectiveCamera1 = cameras.createNode('perspectiveCamera');
			perspectiveCamera1.setName('perspectiveCamera1');
			const perspectiveCamera1_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera1.uiData.setPosition(0, -50);
			perspectiveCamera1.p.position.set([-4.897888009753616, 2.7159903561185454, 0.6542743794164616]);
			perspectiveCamera1.p.rotation.set([-71.59274675167363, -67.06969752764905, -70.13265628647652]);
			perspectiveCamera1.params.postCreateSpareParams();
			perspectiveCamera1.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera1, children: perspectiveCamera1_nodes};
		}
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([0, 0.75, 0]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_cameraCSSRenderer1(cameras: GeoObjNode) {
			var cameraCSSRenderer1 = cameras.createNode('cameraCSSRenderer');
			cameraCSSRenderer1.setName('cameraCSSRenderer1');
			const cameraCSSRenderer1_nodes: ReturnedNodeDataDict = {};
			function create_CSS2DRenderer1(cameraCSSRenderer1: CameraCSSRendererSopNode) {
				var CSS2DRenderer1 = cameraCSSRenderer1.createNode('CSS2DRenderer');
				CSS2DRenderer1.setName('CSS2DRenderer1');
				const CSS2DRenderer1_nodes: ReturnedNodeDataDict = {};
				CSS2DRenderer1.uiData.setPosition(0, 0);
				CSS2DRenderer1.p.css.set(
					'.CSS2DObject {\n	color: white;\n	background-color: black;\n	padding: 5px 10px;\n	border: 1px solid blue;\n	border-radius: 5px;\n	pointer-events: auto;\n	user-select: none;\n}\n.CSS2DObject2 {\n	color: white;\n	background-color: green;\n	padding: 5px 10px;\n	border: 1px solid blue;\n	border-radius: 5px;\n	pointer-events: auto;\n	user-select: none;\n}'
				);
				CSS2DRenderer1.params.postCreateSpareParams();
				CSS2DRenderer1.params.runOnSceneLoadHooks();
				return {node: CSS2DRenderer1, children: CSS2DRenderer1_nodes};
			}
			cameraCSSRenderer1_nodes['CSS2DRenderer1'] = create_CSS2DRenderer1(cameraCSSRenderer1);
			if (cameraCSSRenderer1.childrenController) {
				cameraCSSRenderer1.childrenController.selection.set([]);
			}
			cameraCSSRenderer1.uiData.setPosition(0, 300);
			cameraCSSRenderer1.flags.display.set(true);
			cameraCSSRenderer1.p.node.set('CSS2DRenderer1');
			cameraCSSRenderer1.params.postCreateSpareParams();
			cameraCSSRenderer1.params.runOnSceneLoadHooks();
			return {node: cameraCSSRenderer1, children: cameraCSSRenderer1_nodes};
		}
		cameras_nodes['perspectiveCamera1'] = create_perspectiveCamera1(cameras);
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['cameraCSSRenderer1'] = create_cameraCSSRenderer1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera1'].node);
		cameras_nodes['cameraCSSRenderer1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([]);
		}
		cameras.uiData.setPosition(-50, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	scene_root_nodes['geo1'] = create_geo1(root);
	scene_root_nodes['ground'] = create_ground(root);
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);

	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/perspectiveCamera1',
		focusedNodes: [scene_root_nodes['geo1'].node.node('data1') as any], //scene_root_nodes['HTMLElement'].node.children(),
	};
}
